/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import {
    Component,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import { aviMediumDateFormatWithSec } from 'ng/modules/core/pipes/avi-date.pipe';
import {
    IAviDataGridConfig,
    IAviDataGridRow,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';

import { VsLogsStore } from '../../../../services/vs-logs.store';
import {
    VsLogListExpandedContentStore,
} from '../vs-log-list-expanded-content.store';
import { FilterOperatorType, VsAppProfileType } from '../../../../vs-logs.types';
import * as l10n from './vs-log-summary-content.l10n';

import './vs-log-summary-content.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description
 *     Component for displaying Summary section in VS log expandeded content.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'vs-log-summary-content',
    templateUrl: './vs-log-summary-content.component.html',
})
export class VsLogSummaryContentComponent {
    @ViewChild('policiesAppliedFieldTemplateRef')
    public readonly policiesAppliedFieldTemplateRef: TemplateRef<HTMLElement>;

    public readonly vsAppProfileType$ = this.vsLogsStore.vsAppProfileType$;

    public readonly VsAppProfileType = VsAppProfileType;

    /**
     * Object that includes L7 client information.
     */
    public readonly l7ClientInfo$ = this.vsLogListExpandedContentStore.l7ClientInfo$;

    /**
     * Object that includes L4 client information.
     */
    public readonly l4ClientInfo$ = this.vsLogListExpandedContentStore.l4ClientInfo$;

    /**
     * Object that includes policy rule information.
     */
    public readonly policyRules$ = this.vsLogListExpandedContentStore.policyRules$;

    public readonly nsxAlbInfo$ = this.vsLogListExpandedContentStore.nsxAlbInfo$;

    /**
     * Object that includes HTTP request information.
     */
    public readonly requestInfo$ = this.vsLogListExpandedContentStore.requestInfo$;

    public readonly requestDetails$ = this.vsLogListExpandedContentStore.requestDetails$;

    /**
     * Object that includes HTTP response information.
     */
    public readonly responseInfo$ = this.vsLogListExpandedContentStore.responseInfo$;

    public readonly dnsClientInfo$ = this.vsLogListExpandedContentStore.dnsClientInfo$;

    public readonly dnsLoadBalancerInfo$ = this.vsLogListExpandedContentStore.dnsLoadBalancerInfo$;

    public readonly ssl$ = this.vsLogListExpandedContentStore.ssl$;

    public readonly hasAuthenticationLogs$ =
        this.vsLogListExpandedContentStore.hasAuthenticationLogs$;

    public readonly icapLog$ = this.vsLogListExpandedContentStore.icapLog$;

    public readonly wafLog$ = this.vsLogListExpandedContentStore.wafLog$;

    public readonly botManagementLog$ = this.vsLogListExpandedContentStore.botManagementLog$;

    public readonly datascriptInfo$ = this.vsLogListExpandedContentStore.datascriptInfo$;

    public readonly FilterOperatorType = FilterOperatorType;

    /**
     * Format string that specifies date transformation to seconds.
     */
    public readonly timeFormat = aviMediumDateFormatWithSec;

    /**
     * Get global keys from source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Hash from enum values to the corresponding translated labels.
     */
    public readonly enumLabelHash = {
        ...this.schemaService.getEnumValueLabelsHash('DnsRecordType'),
        ...this.schemaService.getEnumValueLabelsHash('Protocol'),
    };

    /** For template use */
    public readonly Boolean = Boolean;

    /**
     * List of selectors of the sections for the cinematic view except for Top-info and Summary.
     * Each section can be also referred to as a "policy".
     * For L7 log use only.
     */
    public policyGridRows: IAviDataGridRow[];

    /**
     * Configuration for the grid to show what policies (as c-view sections, not the VS policies)
     * are populated.
     */
    public policyGridConfig: IAviDataGridConfig;

    constructor(
        private readonly schemaService: SchemaService,
        private readonly vsLogsStore: VsLogsStore,
        private readonly vsLogListExpandedContentStore: VsLogListExpandedContentStore,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }
}
